import { BodyText, Skeleton } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { TRACKERS } from "@/constants/Trackers";
import { roomActionsInProgressAtom } from "@/features/Charting/atoms/roomActions";
import { ChatModeIndicator } from "@/features/Chat/components/ChatModeIndicator";
import { DictationTextarea } from "@/features/Chat/components/DictationTextarea";
import { ChatFilterMode } from "@/features/Chat/types/chatMessageTypes";
import { useDraftMessage } from "@/hooks/useDraftMessage";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { useEncounter } from "@/network/useEncounter";
import { useUser } from "@/network/useUser";
import { useParams, useSearch } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { Video } from "lucide-react";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  ChatActionType,
  type ChatActionType as ChatActionTypeType,
  REASON_LABELS,
  useChatAvailableActions,
} from "../../network/useChatAvailableActions";
import type { OutgoingChatMessage } from "../types/universalChatTypes";
import { MessageType, SenderType } from "../types/universalChatTypes";

interface UniversalChatInputProps {
  onSendMessage: (message: OutgoingChatMessage) => void;
  disabled?: boolean;
  roomId: string;
  toggleVideoVisitCall?: () => void;
  isCallPending?: boolean;
}

export const UniversalChatInput = ({
  onSendMessage,
  disabled = false,
  roomId,
  toggleVideoVisitCall,
  isCallPending = false,
}: UniversalChatInputProps) => {
  const { data: user } = useUser();
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });

  const {
    draftMessage: messageText,
    setDraftMessage: setMessageText,
    clearDraftMessage,
  } = useDraftMessage("chat", patientId, encounterId?.toString());

  const isRoomActionInProgress = useAtomValue(roomActionsInProgressAtom);

  const { data: chatFilterEnabled } = useConfigCatFeatureFlag("decouple-chat");
  const { data: encounter } = useEncounter(encounterId as string, patientId);
  const showAvComplianceBanner =
    (encounter?.isAv && !encounter?.isTextBased) ?? false;

  const {
    data: { actions, chatMode } = {},
    isPending: isAvailableActionsPending,
    isError: isAvailableActionsError,
  } = useChatAvailableActions({
    patientId: patientId as string,
    encounterId: encounterId as string | undefined,
  });

  const handleSendMessage = useCallback(() => {
    if (!messageText.trim() || disabled || !user?.professional.fhir.id) return;

    const message = {
      encounterId: encounterId?.toString() || "",
      roomId: roomId,
      clientId: uuidv4(),
      senderId: user.professional.fhir.id,
      senderType: SenderType.CLINICIAN,
      type: MessageType.TEXT,
      body: {
        text: messageText.trim(),
        markdown: false,
      },
      createdAt: new Date().toISOString(),
      processingStatus: "UNPROCESSED",
    };

    onSendMessage(message);
    clearDraftMessage();
  }, [
    messageText,
    onSendMessage,
    disabled,
    roomId,
    encounterId,
    user?.professional.fhir.id,
    clearDraftMessage,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const isActionBlocked = (action: ChatActionTypeType) => {
    return !isAvailableActionsPending && Boolean(actions?.[action]?.length);
  };

  let determinedUnavailableReason: string | undefined;

  if (isRoomActionInProgress) {
    determinedUnavailableReason = "Room action in progress";
  } else if (isAvailableActionsError || !chatMode) {
    // Handle query failure or missing chatMode
    determinedUnavailableReason = "Chat is currently unavailable";
  } else if (isActionBlocked(chatMode)) {
    // chatMode is defined, but the specific action for this mode is not permitted.
    const reasonCode = actions?.[chatMode]?.[0];
    determinedUnavailableReason =
      reasonCode && reasonCode in REASON_LABELS
        ? REASON_LABELS[reasonCode]
        : "Chat restricted"; // Fallback if no specific reason code from backend or mapping.
  }
  // If none of the above conditions were met, determinedUnavailableReason remains undefined,
  // meaning the chat is available.

  const isChatUnavailable = Boolean(determinedUnavailableReason);
  // This variable is used in the placeholder text.
  const chatUnavailableReason = determinedUnavailableReason;

  // Check if video call is available
  const isVideoCallAvailable =
    encounterId &&
    toggleVideoVisitCall &&
    !isActionBlocked(ChatActionType.VIDEO_CALL);

  // Get placeholder text based on state
  const getPlaceholderText = () => {
    if (isChatUnavailable) {
      return `Chat is unavailable: ${chatUnavailableReason}`;
    }
    // Match the placeholder text from ChatInputBar for consistency
    return chatFilterEnabled ? "Type a live message..." : "Type a message...";
  };

  if (isAvailableActionsPending) {
    return (
      <div className="p-3 border-t flex gap-2 items-center w-full">
        <Skeleton className="h-10 flex-1" />
        <Skeleton className="h-8 w-14" />
      </div>
    );
  }

  const inputContent = (
    <>
      {encounterId &&
        showAvComplianceBanner &&
        !isActionBlocked(ChatActionType.VIDEO_CALL) && (
          <div className="bg-superclinical p-2 leading-none rounded-md">
            <BodyText size="sm">
              This is an AV visit due to compliance needs or by patient request.
              Click Video icon below to start.
            </BodyText>
          </div>
        )}

      <div className="flex gap-2 items-center">
        {isVideoCallAvailable && (
          <Button
            size="sm"
            variant="icon"
            onClick={toggleVideoVisitCall}
            disabled={isCallPending}
            data-testid="video-call-button"
          >
            <Video className="w-4 h-4" />
          </Button>
        )}
        <DictationTextarea
          value={messageText}
          onChange={setMessageText}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholderText()}
          disabled={disabled || isChatUnavailable}
          disableDictation={isChatUnavailable}
          encounterId={encounterId as string | undefined}
          className="max-h-32 resize-none"
          trackerStart={TRACKERS.DICTATION_CHAT_START}
          trackerEnd={TRACKERS.DICTATION_CHAT_END}
          data-testid="chat-input-textbox"
        />
        <Button
          size="sm"
          onClick={handleSendMessage}
          disabled={!messageText.trim() || disabled || isChatUnavailable}
        >
          Send
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col gap-2 p-3 border-t w-full">
      {chatFilterEnabled ? (
        <ChatModeIndicator filterMode={ChatFilterMode.LIVE}>
          {inputContent}
        </ChatModeIndicator>
      ) : (
        inputContent
      )}
    </div>
  );
};
